import React, { useMemo, useState, useEffect } from 'react'
import {
  Table,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableBodyCell,
  NumberInput,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { Currency } from '@vend/business-components'
import { formatCurrencyValue, getCurrencyFormatParts } from '@vend/utilities'
import { getCurrencyDenominations } from '../../../constants/currency-denomination'

import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { DenominationQuantity } from '../../../util/register-closure-types'
interface RegisterClosureCashCountingProps {
  currencyCode: string
  value: string
  paymentName: string
  onChange: (total: string) => void
  onDenominationChanged?: (denominations: DenominationQuantity[]) => void
}

export const RegisterClosureCashCounting: React.FC<RegisterClosureCashCountingProps> = ({
  currencyCode,
  paymentName,
  value,
  onChange,
  onDenominationChanged,
}) => {
  const currentOutlet = useRegisterManager().getActiveOutlet()
  const retailer = useRetailerSettingsManager().get()

  const denominations: number[] = useMemo(() => {
    return getCurrencyDenominations(currencyCode)
  }, [currencyCode])

  const decimalSeparator = useMemo(
    () =>
      getCurrencyFormatParts(
        retailer?.culture || '',
        currentOutlet.currency,
        currentOutlet.currency_symbol
      ).decimal,
    [retailer?.culture, currentOutlet.currency, currentOutlet.currency_symbol]
  )

  const [quantities, setQuantities] = useState<number[]>(() =>
    denominations.map(() => 0)
  )
  const [customAmount, setCustomAmount] = useState<string>('')
  const [total, setTotal] = useState<string>(value || '0.00')

  useEffect(() => {
    setTotal(value || '0.00')
  }, [value])

  useEffect(() => {
    const denominationsTotal = quantities.reduce(
      (sum, qty, i) => sum + qty * denominations[i],
      0
    )
    const customAmountValue = parseFloat(customAmount) || 0
    const newTotal = denominationsTotal + customAmountValue
    const rounded = newTotal.toFixed(2)
    const denominationMap = denominations.map((denomination, index) => {
      return {
        denomination,
        quantity: quantities[index],
      }
    })
    setTotal(rounded)
    onChange(rounded)
    if (onDenominationChanged) {
      onDenominationChanged(denominationMap)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantities, customAmount])

  const handleQuantityChange = (index: number, qty: number) => {
    setQuantities(quantities => {
      const updatedQuantities = [...quantities]
      updatedQuantities[index] = qty
      return updatedQuantities
    })
  }

  const handleCustomAmountChange = (val: number) => {
    setCustomAmount(val.toString())
  }

  if (!retailer) {
    return null
  }

  return (
    <Table className="wr-closure-cash-counting">
      <TableHead>
        <TableRow>
          <TableHeadCell className="vd-align-right">
            <T _str="Denomination" />
          </TableHeadCell>
          <TableHeadCell className="vd-align-right">
            <T _str="Quantity" />
          </TableHeadCell>
          <TableHeadCell className="vd-align-right">
            <T _str="Amount" />
          </TableHeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {denominations.map((denom: number, index: number) => (
          <TableRow key={`denomination-row-${index}`}>
            <TableBodyCell className="vd-align-right">
              {formatCurrencyValue(
                denom,
                retailer.culture,
                currentOutlet.currency,
                currentOutlet.currency_symbol
              )}
            </TableBodyCell>
            <TableBodyCell
              data-testid="denomination-input-parent"
              className="vd-align-right wr-denomination-input"
            >
              <NumberInput
                min={0}
                max={1000000}
                value={quantities[index]}
                data-testid={`denomination-input-${index}`}
                onChange={val => handleQuantityChange(index, Number(val))}
                validators={{
                  nonDecimal: true,
                }}
                selectTextOnClick
              />
            </TableBodyCell>
            <TableBodyCell className="vd-align-right">
              <Currency value={(quantities[index] * denom).toFixed(2)} />
            </TableBodyCell>
          </TableRow>
        ))}
        <TableRow>
          <TableBodyCell className="vd-align-right">
            <strong>
              <T _str="Custom Amount" />
            </strong>
          </TableBodyCell>
          <TableBodyCell className="vd-align-right">—</TableBodyCell>
          <TableBodyCell className="vd-align-right">
            <NumberInput
              value={customAmount}
              onChange={handleCustomAmountChange}
              placeholder="0.00"
              decimalSeparator={decimalSeparator}
              data-testid="custom-amount-input"
            />
          </TableBodyCell>
        </TableRow>
        <TableRow className="total-row">
          <TableBodyCell colSpan={2} className="vd-align-left">
            <strong>
              <T
                _str="{paymentName} TOTAL"
                paymentName={paymentName.toUpperCase()}
              />
            </strong>
          </TableBodyCell>
          <TableBodyCell className="vd-align-right">
            <strong data-testid="cash-counting-total">
              {formatCurrencyValue(
                total,
                retailer.culture,
                currentOutlet.currency,
                currentOutlet.currency_symbol
              )}
            </strong>
          </TableBodyCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}
